<template>
  <v-container fluid class="mt-4">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title class="text-h4 mb-4">
            <v-icon class="mr-3">mdi-account-circle</v-icon>
            Редактирование профиля
          </v-card-title>

          <v-card-text>
            <!-- Success/Error Messages -->
            <v-alert
              v-if="message"
              :type="messageType"
              closable
              class="mb-4"
              @click:close="clearMessage"
            >
              {{ message }}
            </v-alert>

            <!-- Profile Information Section -->
            <v-expansion-panels v-model="expandedPanels" multiple>
              
              <!-- Username Change -->
              <v-expansion-panel value="username">
                <v-expansion-panel-title>
                  <v-icon class="mr-3">mdi-at</v-icon>
                  Изменение имени пользователя
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form @submit.prevent="updateUsername" ref="usernameForm">
                    <v-text-field
                      v-model="usernameForm.username"
                      label="Новое имя пользователя"
                      variant="outlined"
                      :rules="usernameRules"
                      :disabled="loading.username"
                    ></v-text-field>

                    <div class="text-end mt-4">
                      <v-btn
                        type="submit"
                        color="primary"
                        :loading="loading.username"
                      >
                        Изменить имя пользователя
                      </v-btn>
                    </div>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- Email Change -->
              <v-expansion-panel value="email">
                <v-expansion-panel-title>
                  <v-icon class="mr-3">mdi-email-edit</v-icon>
                  Изменение email
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form @submit.prevent="updateEmail" ref="emailForm">
                    <v-text-field
                      v-model="emailForm.email"
                      name="email"
                      autocomplete="email"
                      label="Новый email"
                      type="email"
                      variant="outlined"
                      :rules="emailRules"
                      :disabled="loading.email"
                    ></v-text-field>

                    <v-text-field
                      v-model="emailForm.currentPassword"
                      name="current-password"
                      autocomplete="current-password"
                      label="Текущий пароль"
                      type="password"
                      variant="outlined"
                      :rules="requiredRules"
                      :disabled="loading.email"
                    ></v-text-field>

                    <div class="text-end mt-4">
                      <v-btn
                        type="submit"
                        color="primary"
                        :loading="loading.email"
                      >
                        Изменить email
                      </v-btn>
                    </div>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- Telegram Contact -->
              <v-expansion-panel value="telegram">
                <v-expansion-panel-title>
                  <v-icon class="mr-3">mdi-telegram</v-icon>
                  Telegram контакт
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form @submit.prevent="updateTelegramContact" ref="telegramForm">
                    <v-text-field
                      v-model="telegramForm.telegram_contact"
                      label="Telegram username"
                      variant="outlined"
                      :rules="telegramRules"
                      :disabled="loading.telegram"
                      hint="Введите username без @, например: username123"
                      persistent-hint
                      prefix="@"
                    ></v-text-field>

                    <div class="text-end mt-4">
                      <v-btn
                        type="submit"
                        color="primary"
                        :loading="loading.telegram"
                      >
                        Сохранить Telegram
                      </v-btn>
                    </div>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- Password Change -->
              <v-expansion-panel value="password">
                <v-expansion-panel-title>
                  <v-icon class="mr-3">mdi-lock-reset</v-icon>
                  Изменение пароля
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form @submit.prevent="updatePassword" ref="passwordForm">
                    <v-text-field
                      v-model="passwordForm.currentPassword"
                      name="current-password"
                      autocomplete="current-password"
                      label="Текущий пароль"
                      type="password"
                      variant="outlined"
                      :rules="requiredRules"
                      :disabled="loading.password"
                      class="mb-4"
                    ></v-text-field>

                    <v-text-field
                      v-model="passwordForm.newPassword"
                      name="new-password"
                      autocomplete="new-password"
                      label="Новый пароль"
                      type="password"
                      variant="outlined"
                      :rules="passwordRules"
                      :disabled="loading.password"
                      hint="Минимум 8 символов, должен содержать заглавную букву, строчную букву и цифру"
                      persistent-hint
                      class="mb-5"
                    ></v-text-field>

                    <v-text-field
                      v-model="passwordForm.confirmPassword"
                      name="confirm-password"
                      autocomplete="new-password"
                      label="Подтверждение пароля"
                      type="password"
                      variant="outlined"
                      :rules="confirmPasswordRules"
                      :disabled="loading.password"
                      class="mb-3"
                    ></v-text-field>

                    <div class="text-end mt-4">
                      <v-btn
                        type="submit"
                        color="primary"
                        :loading="loading.password"
                      >
                        Изменить пароль
                      </v-btn>
                    </div>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>

            </v-expansion-panels>

            <!-- Current Profile Info -->
            <v-divider class="my-6"></v-divider>
            
            <div class="text-h6 mb-4">Текущая информация</div>
            <v-list>
              <v-list-item>
                <v-list-item-title>Имя пользователя:</v-list-item-title>
                <v-list-item-subtitle>{{ profile.username || 'Не указано' }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Email:</v-list-item-title>
                <v-list-item-subtitle>
                  {{ profile.email || 'Не указан' }}
                  <v-chip
                    v-if="!profile.is_email_confirmed"
                    size="small"
                    color="warning"
                    class="ml-2"
                  >
                    Не подтвержден
                  </v-chip>
                  <v-chip
                    v-else
                    size="small"
                    color="success"
                    class="ml-2"
                  >
                    Подтвержден
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Telegram:</v-list-item-title>
                <v-list-item-subtitle>{{ profile.telegram_contact ? `@${profile.telegram_contact}` : 'Не указан' }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <!-- Limits of the tariff plan -->
            <template v-if="usage">
              <v-divider class="my-4"></v-divider>
              <h3 class="text-h6 mb-2">
                Тариф{{ usage.plan.plan_name ? `: ${usage.plan.plan_name}` : '' }}
                <HelpHint section="plan" text="Что ограничивает тариф и как освободить лимит" />
              </h3>
              <template v-if="usage.limit != null">
                <div class="d-flex justify-space-between text-body-2 mb-1">
                  <span>Найденные сообщения за месяц</span>
                  <span>{{ formatCount(usage.used) }} из {{ formatCount(usage.limit) }} до {{ resetDate(usage.resetsAt) }}</span>
                </div>
                <v-progress-linear :model-value="Math.min(100, usage.used / usage.limit * 100)" height="8" rounded
                                   :color="usage.level === 'full' ? 'error' : usage.level === 'warn' ? 'warning' : 'primary'"></v-progress-linear>
                <div class="text-caption text-medium-emphasis mt-2">
                  Считаются сообщения, найденные проектами в этом месяце. Когда лимит исчерпан, мониторинг проектов
                  останавливается до 1 числа следующего месяца. Ненужные найденные сообщения можно удалить
                  кнопкой под фильтрами на странице «Сообщения» — это освобождает лимит.
                </div>
              </template>
              <div v-else class="text-body-2">Найденные сообщения: без ограничений</div>
              <div class="text-body-2 mt-3">
                Запущенные проекты: {{ usage.plan.active_projects }}{{ usage.plan.max_active_projects != null ? ` из ${usage.plan.max_active_projects}` : '' }}
              </div>
              <div class="text-body-2">
                «Мои источники»: {{ usage.plan.user_sources }}{{ usage.plan.max_user_sources != null ? ` из ${usage.plan.max_user_sources}` : '' }}
              </div>
            </template>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'
import { loadPlanUsage, resetDate, formatCount } from '@/utils/planUsage'

export default {
  name: 'Profile',
  data() {
    return {
      profile: {
        username: '',
        email: '',
        telegram_contact: '',
        is_email_confirmed: false
      },
      usernameForm: {
        username: ''
      },
      telegramForm: {
        telegram_contact: ''
      },
      emailForm: {
        email: '',
        currentPassword: ''
      },
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      loading: {
        telegram: false,
        username: false,
        email: false,
        password: false
      },
      usage: null,
      message: '',
      messageType: '',
      expandedPanels: ['telegram'], // Open telegram panel by default
      
      // Validation rules
      telegramRules: [
        v => !v || (v && v.length >= 5) || 'Telegram username должен быть минимум 5 символов',
        v => !v || (v && v.length <= 32) || 'Telegram username должен быть максимум 32 символа',
        v => !v || (v && /^[a-zA-Z0-9_]+$/.test(v)) || 'Только буквы, цифры и подчеркивания'
      ],
      usernameRules: [
        v => !!v || 'Имя пользователя обязательно',
        v => (v && v.trim().length >= 3) || 'Имя пользователя должно быть минимум 3 символа',
        v => (v && v.length <= 50) || 'Имя пользователя должно быть максимум 50 символов'
      ],
      emailRules: [
        v => !!v || 'Email обязателен',
        v => (v && /.+@.+\..+/.test(v)) || 'Email должен быть действительным'
      ],
      requiredRules: [
        v => !!v || 'Поле обязательно для заполнения'
      ],
      passwordRules: [
        v => !!v || 'Пароль обязателен',
        v => (v && v.length >= 8) || 'Пароль должен содержать минимум 8 символов',
        v => (v && /[A-Z]/.test(v)) || 'Пароль должен содержать заглавную букву',
        v => (v && /[a-z]/.test(v)) || 'Пароль должен содержать строчную букву',
        v => (v && /\d/.test(v)) || 'Пароль должен содержать цифру'
      ]
    }
  },
  computed: {
    confirmPasswordRules() {
      return [
        v => !!v || 'Подтверждение пароля обязательно',
        v => (v === this.passwordForm.newPassword) || 'Пароли не совпадают'
      ]
    }
  },
  async mounted() {
    await this.loadProfile()
    this.loadUsage()
  },
  methods: {
    resetDate,
    formatCount,
    async loadUsage() {
      try {
        this.usage = await loadPlanUsage()
      } catch (e) {
        this.usage = null
      }
    },
    async loadProfile() {
      try {
        const response = await apiClient.get('/users/me/profile')
        this.profile = response.data
        
        // Initialize forms with current data
        this.usernameForm.username = this.profile.username
        this.telegramForm.telegram_contact = this.profile.telegram_contact || ''
        this.emailForm.email = this.profile.email
        
      } catch (error) {
        this.showMessage('Ошибка загрузки профиля', 'error')
      }
    },
    
    async updateTelegramContact() {
      const { valid } = await this.$refs.telegramForm.validate()
      if (!valid) return
      
      this.loading.telegram = true
      try {
        await apiClient.put('/users/me/telegram', {
          telegram_contact: this.telegramForm.telegram_contact || null
        })
        
        this.profile.telegram_contact = this.telegramForm.telegram_contact
        this.showMessage('Telegram контакт успешно обновлен', 'success')
        
        // Update Vuex store
        await this.$store.dispatch('initializeAuth')
        
      } catch (error) {
        this.showMessage(
          error.response?.data?.detail || 'Ошибка обновления Telegram контакта',
          'error'
        )
      } finally {
        this.loading.telegram = false
      }
    },
    
    async updateUsername() {
      const { valid } = await this.$refs.usernameForm.validate()
      if (!valid) return
      
      this.loading.username = true
      try {
        // Сервер обрезает пробелы по краям — показываем то же, что сохранено
        const username = this.usernameForm.username.trim()
        await apiClient.put('/users/me/username', { username })

        this.usernameForm.username = username
        this.profile.username = username
        this.showMessage('Имя пользователя успешно изменено', 'success')
        
        // Update Vuex store
        await this.$store.dispatch('initializeAuth')
        
      } catch (error) {
        this.showMessage(
          error.response?.data?.detail || 'Ошибка изменения имени пользователя',
          'error'
        )
      } finally {
        this.loading.username = false
      }
    },
    
    async updateEmail() {
      const { valid } = await this.$refs.emailForm.validate()
      if (!valid) return
      
      this.loading.email = true
      try {
        await apiClient.put('/users/me/email', {
          email: this.emailForm.email,
          current_password: this.emailForm.currentPassword
        })
        
        this.profile.email = this.emailForm.email
        this.profile.is_email_confirmed = false
        
        // Clear form completely
        this.emailForm.currentPassword = ''
        
        // Reset only password field validation, keep email value
        this.$nextTick(() => {
          if (this.$refs.emailForm) {
            this.$refs.emailForm.resetValidation()
          }
        })
        
        this.showMessage(
          'Email изменен. Проверьте новый почтовый ящик для подтверждения.',
          'success'
        )
        
        // Don't update Vuex store to avoid logout - wait for email confirmation
        
      } catch (error) {
        this.showMessage(
          error.response?.data?.detail || 'Ошибка изменения email',
          'error'
        )
      } finally {
        this.loading.email = false
      }
    },
    
    async updatePassword() {
      const { valid } = await this.$refs.passwordForm.validate()
      if (!valid) return
      
      this.loading.password = true
      try {
        await apiClient.put('/users/me/password', {
          current_password: this.passwordForm.currentPassword,
          new_password: this.passwordForm.newPassword,
          confirm_password: this.passwordForm.confirmPassword
        })
        
        // Clear form
        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }

        // Empty fields must not show the "required" errors (as in the email form)
        this.$nextTick(() => {
          if (this.$refs.passwordForm) {
            this.$refs.passwordForm.resetValidation()
          }
        })
        
        this.showMessage('Пароль успешно изменен', 'success')
        
      } catch (error) {
        this.showMessage(
          error.response?.data?.detail || 'Ошибка изменения пароля',
          'error'
        )
      } finally {
        this.loading.password = false
      }
    },
    
    showMessage(message, type) {
      this.message = message
      this.messageType = type
    },
    
    clearMessage() {
      this.message = ''
      this.messageType = ''
    }
  }
}
</script>

<style scoped>
.v-expansion-panel-title {
  font-weight: 500;
}

.v-list-item-title {
  font-weight: 500;
}

.v-list-item-subtitle {
  color: rgba(0, 0, 0, 0.6);
}
</style>